import React, { useEffect, useMemo, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../ui/LatexRenderer';
import { motion } from 'framer-motion';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
export function MatchingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.interactiveConfig || {};
  const pairs = config.pairs || []; // [{left: "A", right: "1"}, ...]
  const prevValidateRef = useRef(false);
  // Create left items (never shuffled)
  const leftItems = useMemo(() => {
    return pairs.map((p: any, i: number) => ({
      id: i,
      content: p.left
    }));
  }, [pairs]);
  // Create right items (shuffled deterministically)
  const rightItems = useMemo(() => {
    const items = pairs.map((p: any, i: number) => ({
      id: i,
      content: p.right
    }));
    // Shuffle deterministically based on question.id
    const shuffled = shuffleWithSeed(items, question.id);
    return shuffled.shuffled;
  }, [pairs, question.id]);
  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [connections, setConnections] = useState<Record<number, number>>({}); // leftId -> rightId
  // Reset state when question changes
  useEffect(() => {
    setSelectedLeft(null);
    setConnections({});
    prevValidateRef.current = false;
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    // Ready when all left items have a connection
    const isReady = Object.keys(connections).length === leftItems.length;
    onReadyChange?.(isReady);
  }, [connections, leftItems.length, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      // Convert connections to array of pairs [[leftId, rightId], ...]
      const pairs = Object.entries(connections).map(([left, right]) => [Number(left), right]);
      onAnswer(JSON.stringify(pairs));
    }
  }, [shouldValidate, connections, onAnswer]);
  const handleLeftClick = (id: number) => {
    // If already connected, remove connection
    if (connections[id] !== undefined) {
      const newConnections = {
        ...connections
      };
      delete newConnections[id];
      setConnections(newConnections);
    }
    setSelectedLeft(id);
  };
  const handleRightClick = (id: number) => {
    if (selectedLeft !== null) {
      // Check if this right item is already connected to another left item
      const existingLeft = Object.keys(connections).find(key => connections[Number(key)] === id);
      const newConnections = {
        ...connections
      };
      if (existingLeft) {
        delete newConnections[Number(existingLeft)];
      }
      newConnections[selectedLeft] = id;
      setConnections(newConnections);
      setSelectedLeft(null);
    }
  };
  return <div className="w-full max-w-2xl mx-auto" data-id="element-4564">
      <p className="text-center text-slate-500 mb-6 text-sm font-medium" data-id="element-4565">
        Klik links en dan rechts om te koppelen
      </p>

      <div className="flex justify-between gap-8" data-id="element-4566">
        {/* Left Column */}
        <div className="flex-1 space-y-4" data-id="element-4567">
          {leftItems.map((item: any) => {
          const isConnected = connections[item.id] !== undefined;
          const isSelected = selectedLeft === item.id;
          return <motion.button key={item.id} onClick={() => handleLeftClick(item.id)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className={`w-full p-4 rounded-xl border-2 text-left transition-all relative
                  ${isSelected ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : isConnected ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white hover:border-indigo-300'}
                `} data-id="element-4568">

                <LatexRenderer content={item.content} data-id="element-4569" />
                {isConnected && <div className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rounded-full" data-id="element-4570" />}
              </motion.button>;
        })}
        </div>

        {/* Right Column */}
        <div className="flex-1 space-y-4" data-id="element-4571">
          {rightItems.map((item: any) => {
          const isConnected = Object.values(connections).includes(item.id);
          return <motion.button key={item.id} onClick={() => handleRightClick(item.id)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className={`w-full p-4 rounded-xl border-2 text-left transition-all relative
                  ${isConnected ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white hover:border-indigo-300'}
                  ${selectedLeft !== null && !isConnected ? 'animate-pulse ring-2 ring-indigo-100' : ''}
                `} data-id="element-4572">

                <LatexRenderer content={item.content} data-id="element-4573" />
                {isConnected && <div className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rounded-full" data-id="element-4574" />}
              </motion.button>;
        })}
        </div>
      </div>
    </div>;
}